import { useMemo } from 'react';
import {
  type PositionRow,
  type PositionState,
  distanceToStopPct,
  pnlPct,
} from './tradingMock';
import { useTradingPositionsQuery } from './tradingApi';
import './trading.css';

// 권위: data/reports/UNIMPLEMENTED_INVENTORY.md §4.3 — Live Trading Positions.
//   `position_state` (ACTIVE 행) → symbol / qty / avg_entry / current / pnl /
//   state / TP1/TP2 / trail_stop / last_corp_action_recalc_at 노출.
//
// 실 API: GET /api/v1/trading/positions (TradingController) — useTradingPositionsQuery.
// 시세 source 부재 시 백엔드가 currentPrice=null 또는 avg_entry 폴백을 보낸다.
// 매퍼 (tradingMappers.toUiPosition) 가 null → NaN 변환 → 본 컴포넌트가 isNaN 분기로
// "-" + tooltip "시세 source 부재" 표시 (별도 시세 stub 연결 시까지).
//
// 본 화면이 화면 2 LivePositions placeholder 도 동시 해소한다 (라우팅 /positions).

interface Props {
  /** 테스트 주입용 — 미지정 시 API 호출. */
  rows?: PositionRow[];
}

const PRICE_UNAVAILABLE_TOOLTIP = '시세 source 부재 — 실시간 가격 stub 연결 대기 중';

function stateBadgeTone(state: PositionState): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (state === 'ACTIVE_FULL' || state === 'INITIAL') return 'neutral';
  if (state === 'ACTIVE_TP1_DONE' || state === 'ACTIVE_TP2_DONE') return 'ok';
  if (state === 'ACTIVE_HALF_STOPPED' || state === 'ACTIVE_FORCED_HALF') return 'warn';
  return 'danger';
}

export default function TradingPositionsPage({ rows: injected }: Props): JSX.Element {
  const enabled = injected === undefined;
  const { data, isLoading, isError, error } = useTradingPositionsQuery({}, { enabled });

  // 주입 우선 (테스트). API 결과 → rows 폴백 → 빈 배열.
  const rows = injected ?? data?.rows ?? [];
  const summary = useMemo(() => summarize(rows), [rows]);

  return (
    <div className="trading-page">
      <header className="trading-page__header">
        <h1 className="trading-page__title">포지션 현황</h1>
        <span className="trading-page__subtitle">position_state · ACTIVE</span>
      </header>

      {!injected && isError && (
        <div className="trading-page__notice" role="alert">
          <strong>오류 —</strong>
          {(error as Error)?.message ?? '포지션 조회 실패'}
        </div>
      )}

      <section className="trading-kpi-row" aria-label="포지션 요약">
        <div className="trading-kpi">
          <span className="trading-kpi__label">보유 종목</span>
          <span className="trading-kpi__value">{summary.count}</span>
        </div>
        <div className="trading-kpi">
          <span className="trading-kpi__label">총 평가금액</span>
          <span className="trading-kpi__value">
            {summary.marketValue == null ? '–' : formatKrw(summary.marketValue)}
          </span>
        </div>
        <div className="trading-kpi">
          <span className="trading-kpi__label">평가손익률</span>
          <span
            className={`trading-kpi__value ${pnlClass(summary.totalPnlPct)}`}
          >
            {summary.totalPnlPct == null ? '–' : formatPct(summary.totalPnlPct)}
          </span>
        </div>
        <div className="trading-kpi">
          <span className="trading-kpi__label">TP1 도달</span>
          <span className="trading-kpi__value">
            {summary.tp1Done} / {summary.count}
          </span>
        </div>
      </section>

      <section className="trading-card" aria-label="포지션 상세 테이블">
        <header className="trading-card__header">
          <h2 className="trading-card__title">보유 포지션</h2>
          <span className="trading-page__subtitle">{rows.length}건</span>
        </header>
        <div className="trading-card__body">
          <table className="trading-table">
            <thead>
              <tr>
                <th>종목</th>
                <th>상태</th>
                <th className="num">수량</th>
                <th className="num">평균단가</th>
                <th className="num">현재가</th>
                <th className="num">수익률</th>
                <th className="num">손절가</th>
                <th className="num">손절까지</th>
                <th>TP1 / TP2</th>
                <th className="num">트레일 스탑</th>
                <th>분류 B audit</th>
              </tr>
            </thead>
            <tbody>
              {!injected && isLoading && (
                <tr>
                  <td colSpan={11} className="trading-table__empty">
                    불러오는 중…
                  </td>
                </tr>
              )}
              {(!isLoading || injected) && rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="trading-table__empty">
                    보유 포지션이 없습니다.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const p = pnlPct(row);
                const distStop = distanceToStopPct(row);
                const hasPrice = Number.isFinite(row.currentPrice);
                return (
                  <tr key={row.symbol}>
                    <td>
                      <div>{row.symbolName}</div>
                      <code style={{ color: 'var(--ink-faint)', fontSize: 'var(--text-xs)' }}>
                        {row.symbol}
                      </code>
                    </td>
                    <td>
                      <span className={`trading-badge trading-badge--${stateBadgeTone(row.state)}`}>
                        {row.state}
                      </span>
                    </td>
                    <td className="num">{row.qty}</td>
                    <td className="num">{formatKrw(row.avgEntry)}</td>
                    <td className="num" title={hasPrice ? undefined : PRICE_UNAVAILABLE_TOOLTIP}>
                      {hasPrice ? formatKrw(row.currentPrice) : '–'}
                    </td>
                    <td
                      className={`num ${pnlClass(p)}`}
                      title={p == null ? PRICE_UNAVAILABLE_TOOLTIP : undefined}
                    >
                      {p == null ? '–' : formatPct(p)}
                    </td>
                    <td className="num">{formatKrw(row.stopLossPrice)}</td>
                    <td
                      className={`num ${distStop != null && distStop < 2 ? 'trading-num--down' : 'trading-num--zero'}`}
                      title={distStop == null ? PRICE_UNAVAILABLE_TOOLTIP : undefined}
                    >
                      {distStop == null ? '–' : formatPct(distStop)}
                    </td>
                    <td>
                      <div className="tp-progress">
                        <span
                          className={`tp-progress__pill${row.tp1Done ? ' tp-progress__pill--done' : ''}`}
                        >
                          TP1
                        </span>
                        <span
                          className={`tp-progress__pill${row.tp2Done ? ' tp-progress__pill--done' : ''}`}
                        >
                          TP2
                        </span>
                      </div>
                    </td>
                    <td className="num">
                      {row.trailStopPrice == null ? '–' : formatKrw(row.trailStopPrice)}
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: 'var(--text-xs)',
                          color: row.lastCorpActionRecalcAt
                            ? 'var(--ink-quiet)'
                            : 'var(--ink-faint)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        {row.lastCorpActionRecalcAt
                          ? formatDate(row.lastCorpActionRecalcAt)
                          : 'n/a'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

interface Summary {
  count: number;
  /** 시세 부재 행이 1건이라도 있으면 null (총합 신뢰 불가). */
  marketValue: number | null;
  totalPnlPct: number | null;
  tp1Done: number;
}

function summarize(rows: PositionRow[]): Summary {
  if (rows.length === 0) {
    return { count: 0, marketValue: 0, totalPnlPct: 0, tp1Done: 0 };
  }
  let marketValue = 0;
  let cost = 0;
  let tp1Done = 0;
  let priceGap = false;
  for (const r of rows) {
    if (!Number.isFinite(r.currentPrice)) {
      priceGap = true;
    } else {
      marketValue += r.currentPrice * r.qty;
    }
    cost += r.avgEntry * r.qty;
    if (r.tp1Done) tp1Done += 1;
  }
  if (priceGap) {
    return { count: rows.length, marketValue: null, totalPnlPct: null, tp1Done };
  }
  const totalPnlPct = cost > 0 ? ((marketValue - cost) / cost) * 100 : 0;
  return { count: rows.length, marketValue, totalPnlPct, tp1Done };
}

function pnlClass(value: number | null): string {
  if (value == null) return 'trading-num--zero';
  if (value > 0.01) return 'trading-num--up';
  if (value < -0.01) return 'trading-num--down';
  return 'trading-num--zero';
}

function formatKrw(value: number): string {
  if (!Number.isFinite(value)) return '–';
  return new Intl.NumberFormat('ko-KR').format(Math.round(value));
}

function formatPct(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
